/**
 * Vendor-neutral "thinking" knob -> provider-specific options.
 *
 * `policy.json` says `thinking: off | low | medium | default` per task; this module turns
 * that into what the Google provider actually accepts for the model family in
 * play. Call sites never set `thinkingConfig` themselves.
 *
 * Gemini 2.5 configures thinking as a token budget (`thinkingBudget`); Gemini 3
 * uses a level (`thinkingLevel`) and cannot switch thinking off. Field names
 * are those of `@ai-sdk/google` 3.0.x (`GoogleGenerativeAIProviderOptions`);
 * check the provider's types again before trusting them for a new family.
 */

export type ThinkingSetting = "off" | "low" | "medium" | "default";

/** Mirrors `thinkingConfig` in `@ai-sdk/google`'s provider options. */
export type GoogleThinkingConfig = {
  thinkingBudget?: number;
  thinkingLevel?: "minimal" | "low" | "medium" | "high";
};

/**
 * Thinking OFF is load-bearing on Flash, not a tuning choice. With it on,
 * Gemini 2.5 Flash answers the insights gather tool loop with
 * MALFORMED_FUNCTION_CALL on step one - no text, no tool call, which the caller
 * can only read as an empty gather. Verified by sweep: every other variable
 * (temperature, streaming, prompt language, tool count) still fails; only
 * thinkingBudget 0 completes the loop. Only 0 and the provider default were
 * tested - a low non-zero budget is unverified.
 *
 * It cannot be applied unconditionally, though: `INSIGHTS_MODEL_ID` routes
 * these analyses to a stronger model, and Gemini 2.5 Pro rejects a zero budget
 * (thinking cannot be disabled there; minimum 128). So the zero budget goes
 * only to the family it was verified against and every unrecognised model id
 * keeps the provider default. Re-test before changing - the alias
 * `gemini-2.5-flash` moves under us, which is how this broke with no deploy on
 * our side.
 */
const VERIFIED_ZERO_BUDGET_MODELS = /^gemini-2\.5-flash(-|$)/i;

/** The family that takes a token budget. */
const BUDGET_MODELS = /^gemini-2\.5-/i;

/** The family that takes a level (`gemini-3-pro-preview`, `gemini-3.1-pro-preview`, ...). */
const LEVEL_MODELS = /^gemini-3(\.\d+)?-/i;

/**
 * Smallest non-zero budget accepted by every 2.5 model (Pro's minimum). Not
 * verified on any task yet: no policy entry uses `low` on a 2.5 model.
 */
const LOW_BUDGET_TOKENS = 128;

/**
 * A budget that survives a prompt with a dozen tools and long tool descriptions.
 * On 2.5 Flash the 128-token `low` budget is exhausted on such a prompt and the
 * model answers with an empty STOP (0 output tokens); verified on the brain's
 * coach agent 2026-10-20 (2-3 of 8 calls empty at 128, 0 of 8 at 1024).
 */
const MEDIUM_BUDGET_TOKENS = 1024;

/**
 * Parse an explicit budget override (e.g. `INSIGHTS_GATHER_THINKING_BUDGET`),
 * in tokens. `-1` is the provider's "dynamic" setting; any other negative
 * value, or a non-integer, is ignored (with a warning) rather than shipped to
 * the API.
 */
export function parseThinkingBudgetOverride(
  raw: string | undefined,
  context: { envName?: string; modelId: string },
): number | undefined {
  const trimmed = raw?.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  if (Number.isInteger(parsed) && (parsed >= 0 || parsed === -1)) return parsed;
  console.warn(
    `${context.envName ?? "thinking budget override"} is not a valid budget; ignoring`,
    { value: trimmed, modelId: context.modelId },
  );
  return undefined;
}

/**
 * The `thinkingConfig` to send for `modelId`, or undefined to leave the
 * provider default alone. An explicit budget override wins on every model;
 * otherwise the policy's `thinking` setting is mapped per family.
 */
export function thinkingConfigFor(
  modelId: string,
  thinking: ThinkingSetting | undefined,
  override?: { raw: string | undefined; envName?: string },
): GoogleThinkingConfig | undefined {
  const id = modelId.trim();
  const budgetOverride = override
    ? parseThinkingBudgetOverride(override.raw, { envName: override.envName, modelId: id })
    : undefined;
  if (budgetOverride !== undefined) return { thinkingBudget: budgetOverride };

  switch (thinking) {
    case "off":
      return VERIFIED_ZERO_BUDGET_MODELS.test(id) ? { thinkingBudget: 0 } : undefined;
    case "low":
      if (BUDGET_MODELS.test(id)) return { thinkingBudget: LOW_BUDGET_TOKENS };
      if (LEVEL_MODELS.test(id)) return { thinkingLevel: "low" };
      return undefined;
    case "medium":
      if (BUDGET_MODELS.test(id)) return { thinkingBudget: MEDIUM_BUDGET_TOKENS };
      if (LEVEL_MODELS.test(id)) return { thinkingLevel: "medium" };
      return undefined;
    default:
      return undefined;
  }
}
